import { Component, Input, OnInit, OnDestroy, Optional } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormArray,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { IMailList } from 'src/app/@interfaces/lists.interface';
import { PortfolioService } from '../../../../../@pages/portfolio/portfolio.service';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { ValidatorsService } from 'src/app/@shared/validator/validators.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AlertsService } from 'src/app/@shared/components/services/alerts/alerts.service';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { VOneIconComponent } from '@shared-components';

@Component({
  selector: 'app-crud-mails',
  templateUrl: './crud-mails.component.html',
  styleUrls: ['./crud-mails.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    FormsModule,
    VOneIconComponent,
    TranslatePipe,
  ],
})
export class CrudMailsComponent implements OnInit, OnDestroy {
  registerForm: UntypedFormGroup = this.fb.group({
    itemRows: this.fb.array([this.createRow()]),
    group_mails: ['', [Validators.required]],
  });

  get rowsArr() {
    return this.registerForm.get('itemRows') as UntypedFormArray;
  }
  unsubscribe$ = new Subject<void>();
  @Input() listMails: Array<IMailList> = [];
  @Input() groupMails: string = '';
  @Input() showOwnHeader: boolean = true;
  @Input() showOwnActions: boolean = true;

  constructor(
    private fb: UntypedFormBuilder,
    private mailList: PortfolioService,
    private router: Router,
    private validatorS: ValidatorsService,
    private translate: TranslateService,
    private alertsService: AlertsService,
    @Optional() private dialogRef: MatDialogRef<unknown>,
  ) {}

  ngOnInit(): void {
    this.mailList
      .getMailList(this.groupMails)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.listMails = result;
      });
    this.setGroupMails(this.groupMails);
  }

  remove(id: number, index: number) {
    this.alertsService
      .showConfirmAlert('You won not be able to revert this change!')
      .then((confirm) => {
        if (confirm.isConfirmed) {
          this.mailList
            .removeMailList(id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((result) => {
              if (!result) {
                Swal.fire(
                  'Error',
                  this.translate.instant('Something was wrong'),
                  'error',
                );
              } else {
                Swal.fire(
                  this.translate.instant('Good'),
                  this.translate.instant('Mail removed'),
                  'success',
                ).then((data) => {
                  if (data.isConfirmed) {
                    if (this.groupMails === 'Portfolio') {
                      this.router
                        .navigateByUrl('/dialog', { skipLocationChange: true })
                        .then(() => {
                          this.router.navigate(['/portfolio']);
                        });
                    } else {
                      this.router
                        .navigateByUrl('/dialog', { skipLocationChange: true })
                        .then(() => {
                          this.router.navigate(['/accountsPayable']);
                        });
                    }
                    this.mailList
                      .getMailList(this.groupMails)
                      .pipe(takeUntil(this.unsubscribe$))
                      .subscribe((result) => {
                        this.listMails = result;
                      });
                  }
                });
              }
            });
        }
      });
  }

  async update(): Promise<void> {
    if (this.registerForm.invalid === true) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: this.translate.instant('Mail Invalid, Please Check!'),
      });
      return;
    }

    const group = this.registerForm.value.group_mails;
    const rows = this.rowsArr.value as { mails: string }[];

    try {
      for (const row of rows) {
        const result = await firstValueFrom(
          this.mailList.createMailList(row.mails as unknown as string[], group),
        );
        if (!result) {
          throw new Error('Failed to create mail');
        }
      }
    } catch {
      Swal.fire(
        this.translate.instant('Something was wrong'),
        this.translate.instant('Failed'),
        'error',
      );
      return;
    }

    Swal.fire({
      title: this.translate.instant('Created'),
      text: this.translate.instant('Mail created successfully!'),
      icon: 'success',
      showCancelButton: false,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Ok',
    }).then((data) => {
      if (data.isConfirmed) {
        if (this.groupMails === 'Portfolio') {
          this.router
            .navigateByUrl('/dialog', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['/portfolio']);
            });
        } else {
          this.router
            .navigateByUrl('/dialog', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['/accountsPayable']);
            });
        }
        this.mailList
          .getMailList(this.groupMails)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((result) => {
            this.listMails = result;
          });
        this.resetRows();
        if (!this.showOwnActions) {
          this.dialogRef?.close();
        }
      }
    });
  }

  private createRow(): UntypedFormGroup {
    return this.fb.group({
      mails: [
        '',
        [
          Validators.required,
          this.validatorS.emailValidator,
          Validators.maxLength(50),
        ],
      ],
    });
  }

  addRow(): void {
    this.rowsArr.push(this.createRow());
  }

  private resetRows(): void {
    while (this.rowsArr.length > 1) {
      this.rowsArr.removeAt(this.rowsArr.length - 1);
    }
    this.rowsArr.at(0).reset();
  }

  arrayvalid(text: string, index: number) {
    const control = this.rowsArr.at(index)?.get(text);
    return control?.invalid && control?.touched;
  }

  setGroupMails(groupMails: string) {
    this.registerForm.patchValue({
      group_mails: groupMails,
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
